/**
 * Character Core Logic - Pure Functions
 *
 * Pure game logic for character progression that has no browser dependencies.
 * Can be used in both browser (character.js) and Node.js (simulator) contexts.
 *
 * Extracted from character.js to separate pure logic from UI updates.
 */

import * as config from '../constants.js';
import { addNotification } from './notifications.js'
import { getSkillDisplayName } from "./util.js"
import { commandXpMultiplier, getUpgradeEffect } from './command-core.js'
import { getBlackoutPerk } from './blackout-core.js'
import { gameState } from './gameState-core.js'
import { debug, isDebug, CH } from './debugLog.js';

const energyRegenRate = 2 * 60 * 1000; // 2 minutes in milliseconds
/**
 * Calculate XP required for next level
 * Exponential scaling: Level 1->2 = 1000 XP
 * @param {number} currentLevel - Current character level
 * @returns {number} XP needed to reach next level
 */
export function calculateXPToNextLevel(currentLevel) {
  // Exponential scaling for long-term progression: Level 1->2 = 1000 XP
  // Target: Level 8 takes 1-2 months of consistent play (~150-600 raids)
  return Math.floor(1000 * Math.pow(1.5, currentLevel - 1));
}

/**
 * Expected max HP for a character at a given level — a pure derivation of the
 * cumulative HP growth that levelUpCore applies (base 100 at level 1). Used by
 * server-side save validation; a test asserts it matches walking levelUpCore so
 * the formula can't drift from the game.
 * @param {number} level
 * @returns {number}
 */
/**
 * Max HP gained on reaching `level`. The single definition of the HP curve -
 * levelUpCore applies it one level at a time, maxHpForLevel sums it.
 * @param {number} level - the level being reached
 * @returns {number}
 */
export function hpGainForLevel(level) {
  return 10 + Math.floor(level / 3) * 5;
}

export function maxHpForLevel(level) {
  let maxHp = 100;
  for (let k = 2; k <= level; k++) maxHp += hpGainForLevel(k);
  return maxHp;
}

/**
 * Skill points required to raise a skill from `currentSkillLevel` to the next.
 * Mirrors the cost curve in upgradeSkillCore. Pure, for server-side validation.
 * @param {number} currentSkillLevel
 * @returns {number}
 */
export function skillPointCostForLevel(currentSkillLevel) {
  return Math.max(1, Math.floor(currentSkillLevel / 3) + 1);
}

/**
 * Total skill points granted to a character by the time it reaches `level` —
 * the starting allotment (3) plus each level-up's grant (levelUpCore's curve).
 * Pure derivation for validation; a test asserts it matches walking levelUpCore.
 * @param {number} level
 * @returns {number}
 */
export function totalSkillPointsGrantedForLevel(level) {
  const cfg = config.progressionScaling.skillPointsPerLevel;
  let total = 3; // starting skill points at creation (level 1)
  for (let l = 2; l <= level; l++) total += cfg.base + Math.floor(l / cfg.levelDivisor) * cfg.increment;
  return total;
}

/**
 * Core leveling logic (no UI updates)
 * Updates character object with level up stats
 * @param {Object} character - Character object from gameState
 */
export function levelUpCore(character) {
  const xpToNext = calculateXPToNextLevel(character.level);
  character.xp -= xpToNext;
  character.level++;

  // Increase max HP (shared curve - see hpGainForLevel)
  const hpIncrease = hpGainForLevel(character.level);
  character.maxHp += hpIncrease;
  character.currentHp = Math.min(character.maxHp, character.currentHp + hpIncrease); // Also heal on level up

  // Award skill points using configurable progression scaling
  const cfg = config.progressionScaling.skillPointsPerLevel;
  const skillPointsGained = cfg.base + Math.floor(character.level / cfg.levelDivisor) * cfg.increment;
  character.skillPoints += skillPointsGained;

  // Synergy layer reveal: one-time announcement when the character crosses
  // the unlock level (per-skill purchases live on the skill cards)
  if (character.level === getSynergyUnlockConfig().minLevel) {
    // Sticky: fires mid-raid (when the player is watching rooms, not the toast
    // row), unlocks a mechanic once, and names an action to take elsewhere.
    addNotification('⚡ Synergies discovered! Teammates\' gear can now upgrade your events — unlock per skill from the character page.', 'success', { sticky: true });
  }
}

/**
 * Level a character straight to targetLevel by walking the normal level-up
 * path, so XP, HP and skill points land exactly as if earned naturally.
 * Used by the Officer Academy Command upgrade for new recruits.
 * @param {Object} character - Character object from gameState
 * @param {number} targetLevel - Level to reach (no-op if already there)
 */
export function applyStartingLevel(character, targetLevel) {
  while (character.level < targetLevel) {
    character.xp += calculateXPToNextLevel(character.level);
    levelUpCore(character);
  }
}

/**
 * Core experience gain logic (no UI updates)
 * Handles XP gain and level up checks
 * @param {Object} character - Character object from gameState
 * @param {number} xpAmount - Amount of XP to gain
 */
export function gainExperienceCore(character, xpAmount) {
  if (!character) return;

  // Legend Bonus (Command layer): +XP per enshrined Legend when owned
  character.xp += Math.round(xpAmount * commandXpMultiplier());

  // Check for level up
  let xpToNext = calculateXPToNextLevel(character.level);
  while (character.xp >= xpToNext) {
    levelUpCore(character);
    xpToNext = calculateXPToNextLevel(character.level);
  }
}

/**
 * Core contractor leveling logic (no UI updates)
 * @param {Object} contractor - Contractor object
 */
export function levelUpContractorCore(contractor) {
  const xpToNext = calculateXPToNextLevel(contractor.level || 1);
  contractor.xp -= xpToNext;
  contractor.level = (contractor.level || 1) + 1;

  // Increase max HP (same shared curve as characters - see hpGainForLevel)
  const hpIncrease = hpGainForLevel(contractor.level);
  contractor.maxHp = (contractor.maxHp || 100) + hpIncrease;
  contractor.currentHp = (contractor.currentHp || contractor.maxHp) + hpIncrease; // Also heal on level up
}

/**
 * Core contractor experience gain logic (no UI updates)
 * @param {Object} contractor - Contractor object
 * @param {number} xpAmount - Amount of XP to gain
 */
export function gainContractorExperienceCore(contractor, xpAmount) {
  if (!contractor) return;

  contractor.xp = (contractor.xp || 0) + xpAmount;

  // Check for level up
  let xpToNext = calculateXPToNextLevel(contractor.level || 1);
  while (contractor.xp >= xpToNext) {
    levelUpContractorCore(contractor);
    xpToNext = calculateXPToNextLevel(contractor.level);
  }
}

// Calculate currency cost for upgrading a skill
export function calculateSkillCurrencyCost(currentLevel) {
    // Progressive cost: Level 1 = 50, Level 2 = 100, Level 3 = 200, etc.
    // Formula: 50 * (1.5^level), rounded to nearest 25
    const baseCost = 50;
    const scalingFactor = 1.5;
    const rawCost = baseCost * Math.pow(scalingFactor, currentLevel);
    return Math.round(rawCost / 25) * 25; // Round to nearest 25 credits
}

/**
 * Skill points the next level costs, from the current one.
 *
 * Exported because the skill tab strip prices the upgrade button from it and
 * the upgrade itself charges from it — two copies of this formula is one copy
 * too many, and the UI silently disagreeing with the charge is the failure.
 */
export function skillPointCostFor(currentLevel) {
    return Math.max(1, Math.floor(currentLevel / 3) + 1);
}

export function upgradeSkillCore(skillKey) {
    // Check if current character is on marketplace
    if (gameState.character && gameState.character.isMarketplace) {
        addNotification('Cannot upgrade skills with a character that is contracted out on the marketplace', 'error');
        return;
    }

    if (gameState.character.skillPoints <= 0) return;

    if (!gameState.character.skills[skillKey]) {
        gameState.character.skills[skillKey] = 0;
    }

    // Calculate costs for next level (increases with current level)
    const currentLevel = gameState.character.skills[skillKey];
    const skillPointCost = skillPointCostFor(currentLevel);
    const currencyCost = calculateSkillCurrencyCost(currentLevel);

    if (gameState.character.skillPoints < skillPointCost) {
        addNotification(`⚠️ Need ${skillPointCost} skill points to upgrade this skill`, 'warning');
        return;
    }

    if (gameState.player.credits < currencyCost) {
        addNotification(`⚠️ Need ${currencyCost} credits to upgrade this skill`, 'warning');
        return;
    }

    gameState.character.skills[skillKey]++;
    gameState.character.skillPoints -= skillPointCost;
    gameState.player.credits -= currencyCost;

    // Update character skills if character exists
    if (gameState.character) {
        gameState.character.skills[skillKey] = gameState.character.skills[skillKey];
    }

    addNotification(`✨ Skill upgraded! ${getSkillDisplayName(skillKey)} is now level ${gameState.character.skills[skillKey]} (-${currencyCost} credits)`, 'success');
}

// ---------------------------------------------------------------------------
// Per-skill synergy unlocks (progressive disclosure): the synergy layer is
// hidden below progressionScaling.synergyUnlocks.minLevel, then bought per
// skill with skill points. A synergy variant can fire only when it touches
// an unlocked skill on either side (the event's skill, or the skill of the
// player's partner device) - see eventSystem-core variant selection.
// ---------------------------------------------------------------------------

export function getSynergyUnlockConfig() {
    return config.progressionScaling?.synergyUnlocks || { minLevel: 4, cost: 2 };
}

/** Outer gate: below minLevel no synergy UI or purchases exist at all. */
export function isSynergyRevealed(character = gameState.character) {
    return (character?.level || 1) >= getSynergyUnlockConfig().minLevel;
}

export function isSynergyUnlocked(skillId, character = gameState.character) {
    return !!character?.synergyUnlocks?.[skillId];
}

/**
 * Buy the synergy unlock for one skill with skill points.
 * Returns { success, reason } - reasons map to UI copy.
 */
export function unlockSkillSynergyCore(skillKey, character = gameState.character) {
    const cfg = getSynergyUnlockConfig();
    if (!character) return { success: false, reason: 'no-character' };
    if (!isSynergyRevealed(character)) return { success: false, reason: 'not-revealed' };
    if (character.skills?.[skillKey] === undefined) return { success: false, reason: 'unknown-skill' };
    if (isSynergyUnlocked(skillKey, character)) return { success: false, reason: 'already-unlocked' };
    if ((character.skillPoints || 0) < cfg.cost) return { success: false, reason: 'insufficient-points' };

    character.skillPoints -= cfg.cost;
    if (!character.synergyUnlocks) character.synergyUnlocks = {};
    character.synergyUnlocks[skillKey] = true;
    return { success: true, cost: cfg.cost };
}

// ---------------------------------------------------------------------------
// Equipped-artifact effect lookups. Only the raid owner's two slots apply
// (contractors carry no artifacts): the skillSlot artifact boosts its own
// skill's checks, the utilitySlot artifact carries one named effect.
// ---------------------------------------------------------------------------

/** Value of the given effect on the equipped utility artifact (0 if none). */
export function getUtilityArtifactBonus(effectId, character = gameState.character) {
    return character?.equippedArtifacts?.utilitySlot?.effects?.[effectId] || 0;
}

/** skill-success-bonus of the equipped skill artifact when it matches skillId. */
export function getSkillArtifactBonus(skillId, character = gameState.character) {
    const artifact = character?.equippedArtifacts?.skillSlot;
    if (!artifact || !skillId || artifact.skill !== skillId) return 0;
    return artifact.effects?.['skill-success-bonus'] || 0;
}

/**
 * Milliseconds per point of energy, after permanent bonuses.
 *
 * This was CALLED by getNextEnergyRegenTime() but never defined anywhere, so
 * that function threw a ReferenceError on any call — it simply had no callers,
 * which is the only reason nobody noticed. Defining it properly is also the
 * natural home for the Overclocked Grid blackout perk: a rate belongs in one
 * place, or "+10% regen" has to be applied at every site that divides by it.
 *
 * A FASTER regen is a SHORTER interval, hence the divide.
 */
export function calculateEnergyRegenRate() {
    // Command's Forward Depot and L3's Overclocked Grid stack ADDITIVELY into
    // one bonus rather than multiplying: two independent 50% boosts should be
    // "twice as fast", not 2.25x, and additive keeps the ceiling legible.
    const bonus = getBlackoutPerk('energy-regen', 0) + getUpgradeEffect('forward-depot', 0);
    return energyRegenRate / (1 + bonus);
}

/**
 * Energy regenerated per hour at the CURRENT bonuses.
 *
 * The single source of truth for regen speed. raidEconomics-core's
 * ENERGY_PER_HOUR is the unmodified baseline and stays that way (analytic
 * ceilings are derived from it); anything simulating or displaying elapsed
 * time must use this, or an upgrade to regeneration silently does nothing.
 */
export function energyPerHour() {
    return 3_600_000 / calculateEnergyRegenRate();
}

export function calculateEnergyRegen(timePassedMs) {
    // Regenerate 1 energy every 2 minutes (120,000 ms)
    // This balances with raid energy cost: raids cost ~1 energy per 10 seconds
    // So a 2-minute raid costs ~12 energy, which takes ~24 minutes to recover (roughly 2x raid time)
    return Math.floor(timePassedMs / calculateEnergyRegenRate());
}

//
// Energy regeneration and health recovery functions
//
// Initialize character management when page loads
export function updateEnergyOverTime() {
  const currentTime = Date.now();
  const timeDiffMs = currentTime - gameState.player.lastEnergyUpdate;
  const energyToRestore = calculateEnergyRegen(timeDiffMs);
  if (energyToRestore > 0) {
    if (gameState.player.energy < gameState.player.maxEnergy) {
        debug(CH.raid, "Restoring energy:", energyToRestore, "at 1 every", energyRegenRate / 1000, "s Time since last update (ms):", timeDiffMs);
        gameState.player.energy = Math.min(gameState.player.maxEnergy, gameState.player.energy + energyToRestore);
    }
    gameState.player.lastEnergyUpdate = currentTime;
  }
}

export function getNextEnergyRegenTime() {
  if (gameState.player.energy >= gameState.player.maxEnergy) return null;

  const currentTime = Date.now();
  const timeSinceLastRegen = currentTime - gameState.player.lastEnergyUpdate;
  const rate = calculateEnergyRegenRate();
  const timeToNextRegen = rate - (timeSinceLastRegen % rate);

  return currentTime + timeToNextRegen;
}

// Health regeneration and contractor recovery system
// Player health regeneration increased to 10 HP per tick (5s) to make natural regen viable
// This makes HP consumables more of a credit sink than a time saver
export function processCharacterHealthRegeneration(member, timePassedMs) {
  // No recovery during active raid
  if (gameState.currentRaid && gameState.currentRaid.raidInProgress) return false; 

  let hasChanges = false;
  if (member.injured && member.recoveryTime) {
    if (Date.now() >= member.recoveryTime) {
      debug(CH.raid, "Recovering team member:", member.name);
      member.injured = false;
      member.currentHp = member.maxHp || 100;
      delete member.recoveryTime;
      hasChanges = true;
    }
  } else if (member.currentHp < (member.maxHp || 100)) {
    const healAmount = Math.round(2 * timePassedMs/1000); // 2 per second
    debug(CH.raid, "Restoring health for", member.name, ":", healAmount, "at 2 every second. Time since last update (ms):", timePassedMs);
    member.currentHp = Math.min(member.maxHp, member.currentHp + healAmount);
    hasChanges = true;
  }
  return hasChanges;
}